<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center"
      @click.self="closeModal"
    >
      <!-- Backdrop with blur -->
      <div
        class="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
      ></div>

      <!-- Modal content -->
      <div
        class="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6"
        @click.stop
      >
        <div class="flex items-start justify-between">
          <!-- Close button -->
          <button
            @click="closeModal"
            class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          <!-- Icon -->
          <div class="flex justify-start mb-4">
            <GuidedTourCircleIcon />
          </div>
        </div>

        <!-- Title -->
        <h2 class="text-lg font-semibold text-gray-900 mb-1">
          <Translation
            name="guided_tour.modal_title"
            default="Start Guided Tour"
          />
        </h2>

        <!-- Description -->
        <p class="text-gray-600 text-sm mb-8">
          <Translation
            name="guided_tour.modal_description"
            default="This short tour highlights the main areas of the Salary Matrix."
          />
        </p>

        <!-- Buttons -->
        <div class="flex gap-3">
          <button
            @click="closeModal"
            class="cursor-pointer flex-1 px-4 py-2.5 text-base font-semibold text-brand-700 bg-brand-25 rounded-lg hover:bg-brand-50"
          >
            <Translation name="guided_tour.cancel_button" default="Cancel" />
          </button>
          <button
            @click="startTour"
            class="flex-1 px-4 py-2.5 text-base font-semibold text-white bg-brand-600 rounded-lg hover:bg-brand-800"
          >
            <Translation name="guided_tour.start_button" default="Start" />
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import GuidedTourCircleIcon from "./icons/GuidedTourCircleIcon.vue";
import Translation from "./aft/Translation.vue";

const isOpen = ref(false);

const openModal = () => {
  isOpen.value = true;
  document.body.style.overflow = "hidden";
};

const closeModal = () => {
  isOpen.value = false;
  document.body.style.overflow = "";
  // Dispatch event so SidebarNavigation can mark tour as seen
  window.dispatchEvent(new CustomEvent("guided-tour:closed"));
};

const startTour = () => {
  closeModal();
  // Emit event for tour start - can be extended later
  window.dispatchEvent(new CustomEvent("guided-tour:start"));
};

const handleEscape = (event) => {
  if (event.key === "Escape" && isOpen.value) {
    closeModal();
  }
};

// Listen for open event from sidebar
const handleOpenRequest = () => {
  openModal();
};

onMounted(() => {
  document.addEventListener("keydown", handleEscape);
  window.addEventListener("guided-tour:open", handleOpenRequest);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleEscape);
  window.removeEventListener("guided-tour:open", handleOpenRequest);
  document.body.style.overflow = "";
});

// Expose methods for parent component
defineExpose({
  openModal,
  closeModal,
});
</script>
